import {LoaderFunctionArgs, useLoaderData, useLocation} from "react-router-dom";
import {PRODUCT_MIX_REPORT, PRODUCT_MIX_REPORT_COUNT, PRODUCT_MIX_ALL_VENDOR_ITEMS} from "./fragment";
import { formatData, colDefs, getColDefs, getGroupedColDefs, getAutoGroupColumnDef, formatPivotData, generatePivotColDefs, PivotRow, formatUserPivotData, generateUserPivotColDefs, UserPivotRow } from "./helpers";
import { ReportFilters } from "../../components/ReportFilters";
import ReportHeader from "../../components/ReportHeader";
import { userStore } from "../../../../store/user";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../../store/organization";
import { ProductMixReportRow } from "./type";
import {useEffect, useRef, useState} from "react";
import { eventStore } from "../../../../store/event";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import { SecondaryButton, ToggleButton } from "../../../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { CsvCell, GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { buildReportCsvHeader } from "../../utils/buildReportCsvHeader";
import {TransactionReportRow} from "../transactions-report/type";
import {TRANSACTION_REPORT, TRANSACTION_REPORT_COUNT} from "../transactions-report/fragment";
import Loader from "../../../../components/Loader";
import CustomSelect from "../../../../components/customSelect";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;
const PAGE_SIZE = 50000;
const DEBOUNCE_DELAY = 100;

const BarChartComponent = ({
  rows,
  selectedDataKey,
  displayType,
}: {
  rows: ProductMixReportRow[];
  selectedDataKey: string;
  displayType: string;
}) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full pb-4">
        <div className="text-gray-500 text-sm">No data to display</div>
      </div>
    );
  }

  const getXAxisKey = () => {
    if (displayType === 'by_day_by_item') return 'transaction_date';
    if (displayType === 'by_location_by_item') return 'location_name';
    if (displayType === 'by_day_by_location_by_item') return 'transaction_date';
    return 'pos_name';
  };

  const dataWithLabels = rows.map(row => {
    let label = '';
    const dateOnly = row.transaction_date ? row.transaction_date.split('T')[0] : row.transaction_date;
    if (displayType === 'by_day_by_item') {
      label = `${dateOnly} - ${row.pos_name}`;
    } else if (displayType === 'by_location_by_item') {
      label = `${row.location_name || 'Unknown'} - ${row.pos_name}`;
    } else if (displayType === 'by_day_by_location_by_item') {
      label = `${dateOnly} - ${row.location_name || 'Unknown'} - ${row.pos_name}`;
    } else {
      label = row.pos_name;
    }
    return { ...row, chartLabel: label };
  });

  const filteredData = dataWithLabels.filter(item => item !== null && item !== undefined);
  const sortedData = filteredData.sort((a, b) => {
    const key = selectedDataKey as keyof ProductMixReportRow;
    const aValue = a[key] as number;
    const bValue = b[key] as number;
    return bValue - aValue;
  });
  const customTickFormatter = (value: number) => {
    return value.toString();
  };

  const isTotalUnitsSold = selectedDataKey === "total_units_sold";
  const limitedData = sortedData.slice(0, 20);
  const usdFormatter = (value: number) => {
    return `$${value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
  };
  function formatDataKey(key: string) {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  const maxProductTypeLength = Math.max(
    ...limitedData.map((item) => (item.chartLabel ? item.chartLabel.length : 0))
  );
  const angleInRadians = (Math.abs(-30) * Math.PI) / 180;
  const textHeight = Math.sin(angleInRadians) * maxProductTypeLength * 10;
  const xAxisHeight = textHeight > 50 ? textHeight : 50;
  return (
    <div className="flex bg-white rounded-md flex-row items-center w-full">
      <div className="overflow-x-auto">
        <div className="flex flex-col">
          <div className="inline-block min-w-full align-middle">
            <BarChart
              width={1200}
              height={350 + xAxisHeight}
              data={limitedData}
              margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="chartLabel"
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
                {...(sortedData.length > 5 && {
                  interval: 0,
                  angle: -35,
                  textAnchor: "end",
                  height: xAxisHeight,
                })}
              />
              <YAxis
                width={100}
                tickFormatter={
                  isTotalUnitsSold ? customTickFormatter : usdFormatter
                }
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
              />
              <Tooltip
                contentStyle={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                  color: "#4a5568",
                }}
                formatter={
                  isTotalUnitsSold ? customTickFormatter : usdFormatter
                }
              />
              <Bar
                dataKey={selectedDataKey}
                fill="#B71C1C"
                name={formatDataKey(selectedDataKey)}
              />
            </BarChart>
          </div>
        </div>

      </div>
    </div>
  );
};
type DisplayType = 'by_item' | 'by_day_by_item' | 'by_location_by_item' | 'by_day_by_location_by_item' | 'by_hour_by_item_pivot' | 'by_user_by_item_pivot';

const displayTypeOptions = [
  { value: 'by_item', label: 'By Item' },
  { value: 'by_day_by_item', label: 'By Day → By Item' },
  { value: 'by_location_by_item', label: 'By Location → By Item' },
  { value: 'by_day_by_location_by_item', label: 'By Day → By Location → By Item' },
  { value: 'by_hour_by_item_pivot', label: 'By Hour → By Item (Pivot)' },
  { value: 'by_user_by_item_pivot', label: 'By User → By Item (Pivot)' },
];

export const ProductMixReport = () => {
  const [data, setData] = useState<ProductMixReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [initialUsernames, setInitialUsernames] = useState<{ label: string; value: string }[]>([]);
  const [initialLocations, setInitialLocations] = useState<{ label: string; value: string }[]>([]);
  const [initialProductTypes, setInitialProductTypes] = useState<{ label: string; value: string }[]>([]);
  const [displayType, setDisplayType] = useState<DisplayType>('by_item');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // "All Vendors" items (RP-2536): one line instead of one per selling vendor,
  // but only when the report isn't filtered to a vendor and isn't split by
  // location. Filtered to a vendor, each vendor still sees its own line.
  const [allVendorItemIds, setAllVendorItemIds] = useState<Set<number>>(new Set());
  const [vendorFiltered, setVendorFiltered] = useState(false);
  const isPivot = displayType === 'by_hour_by_item_pivot' || displayType === 'by_user_by_item_pivot';
  const foldAllVendors = !vendorFiltered && (displayType === 'by_item' || displayType === 'by_day_by_item');
  const formattedData = isPivot ? [] : formatData(data, displayType, foldAllVendors ? allVendorItemIds : undefined);
  const pivotResult = displayType === 'by_hour_by_item_pivot'
    ? formatPivotData(data)
    : displayType === 'by_user_by_item_pivot'
      ? formatUserPivotData(data)
      : { rows: [], totalRow: [], itemNames: [] };

  const chartRows = formattedData.filter((row) => row.pos_name !== "Total" && !row.isGroupRow);

  const flattenData = (data: ProductMixReportRow[]): ProductMixReportRow[] => {
    const result: ProductMixReportRow[] = [];
    data.forEach(row => {
      if (row.pos_name !== "Total") {
        result.push(row);
        if (row.isGroupRow && row.detailRows && expandedRows.has(row.id)) {
          row.detailRows.forEach(detailRow => {
            result.push(detailRow);
            if (detailRow.isGroupRow && detailRow.detailRows && expandedRows.has(detailRow.id)) {
              detailRow.detailRows.forEach(nestedDetailRow => {
                result.push(nestedDetailRow);
              });
            }
          });
        }
      }
    });
    return result;
  };

  const tableRows = isPivot
    ? pivotResult.rows
    : displayType === 'by_item'
      ? formattedData.filter((row) => row.pos_name !== "Total")
      : flattenData(formattedData);
  const totalRow = isPivot
    ? pivotResult.totalRow
    : formattedData.filter((row) => row.pos_name === "Total");

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const currentColDefs = displayType === 'by_hour_by_item_pivot'
    ? generatePivotColDefs(pivotResult.itemNames)
    : displayType === 'by_user_by_item_pivot'
      ? generateUserPivotColDefs(pivotResult.itemNames)
      : displayType === 'by_item'
        ? getColDefs()
        : getGroupedColDefs(displayType, expandedRows, toggleRowExpansion);
  const [selectedDataKey, setSelectedDataKey] = useState("total_net_sales");
  const handleDataKeyChange = (newDataKey: string) => {
    setSelectedDataKey(newDataKey);
  };
  const gridRef = useRef<GridApi>();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `ProductMixSalesReport_${timestamp}.csv`,
        columnSeparator: ",",
        prependContent: buildReportCsvHeader({
          reportName: "Product Mix Report",
          searchString: location.search,
        }),
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  const location = useLocation();
  const fetchTimeoutRef = useRef<any>(null);

  useEffect(() => {
    let isMounted = true;

    const debouncedFetch = () => {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setTimeout(fetchTransactions, DEBOUNCE_DELAY);
    };
    const fetchTransactions = async () => {
      setIsLoading(true);
      try {
        const rawOrganizationId = getOrganizationState().organizationId;
        const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
        const { eventId } = getEventState();
        const user = getState().user;

        const search = new URLSearchParams(location.search);
        const where = search.get("where");
        // RP-1937: no filter in the address yet; ReportFilters re-applies it and this runs again.
        if (!where) return;
        let whereVariable: any = where ? JSON.parse(where) : {};
        if (organizationId && organizationId !== 0) {
          // Only apply user vendors filter if no vendor_id is already in the search params
          if (user?.vendors && !whereVariable.vendor_id) {
            whereVariable = {
              ...whereVariable,
              vendor_id: {
                _in: user?.vendors,
              },
            };
          }

          whereVariable = {
            ...whereVariable,
            organization_id: {
              _eq: organizationId,
            },
          };
        }

        if (eventId) {
          whereVariable = {
            ...whereVariable,
            event_id: {
              _eq: eventId,
            },
          };
        } else if (user?.events) {
          whereVariable = {
            ...whereVariable,
            event_id: {
              _in: user?.events,
            },
          };
        }
        console.log({whereVariable})
        const { data: totalData } = await client.query({
          query: PRODUCT_MIX_REPORT_COUNT,
          variables: {
            where: whereVariable,
            limit: 0,
          },
        });
        const totalCount = totalData.total.aggregate.count;
        console.log(totalCount);
        let offset = 0;
        let chunkData: ProductMixReportRow[] = [];

        while (offset < totalCount) {
          const { data: chunk } = await client.query({
            query: PRODUCT_MIX_REPORT,
            variables: {
              where: whereVariable,
              limit: PAGE_SIZE,
              offset: offset,
            },
          });
          chunkData = [...chunkData, ...(chunk.reports_product_mix || [])];
          offset += PAGE_SIZE;
        }

        if (isMounted){
          const newLocations = chunkData
              .filter((row) => row.location_id && row.location_name)
              .map((row) => ({
                label: row.location_name.toString(),
                value: row.location_id.toString(),
              }));

          setInitialLocations((prevLocations) => {
            const combined = [...prevLocations, ...newLocations];

            const uniqueMap = new Map<string, string>();
            for (const loc of combined) {
              if (loc.value) {
                uniqueMap.set(loc.value, loc.label);
              }
            }

            return [...uniqueMap.entries()]
                .sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: "base" }))
                .map(([value, label]) => ({ label, value }));
          });
        }

        // Which of these items are set to "All Vendors" (RP-2536). Best effort:
        // if the lookup fails the report still shows, just split by vendor.
        let allVendorIds = new Set<number>();
        try {
          const itemIds = Array.from(new Set(
              chunkData
                  .map((row) => row.unique_id?.item_id)
                  .filter((id): id is number => typeof id === 'number')
          ));
          for (let i = 0; i < itemIds.length; i += 1000) {
            const { data: itemData } = await client.query({
              query: PRODUCT_MIX_ALL_VENDOR_ITEMS,
              variables: { ids: itemIds.slice(i, i + 1000) },
            });
            for (const item of itemData?.items || []) allVendorIds.add(item.id);
          }
        } catch (lookupError) {
          console.error("All Vendors item lookup failed:", lookupError);
          allVendorIds = new Set();
        }

        if (isMounted) {
          setAllVendorItemIds(allVendorIds);
          setVendorFiltered(Boolean(whereVariable.vendor_id));
          setData(chunkData);
          const newUsernames = chunkData.map((row) => row.username);
          setInitialUsernames((prevUsernames) => {
            const updatedUsernames = Array.from(
                new Set([...prevUsernames.map((u) => u.value), ...newUsernames])
            )
                .filter((username) => username)
                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
            return updatedUsernames.map((username) => ({
              label: username,
              value: username,
            }));
          });

          // Derive the Product Type dropdown options the same way Username does.
          // Values come straight from the rows we just fetched (no separate
          // lookup table for product_type), so we dedupe + sort.
          const newProductTypes = chunkData
              .map((row) => row.product_type)
              .filter((pt): pt is string => !!pt);
          setInitialProductTypes((prev) => {
            const merged = Array.from(
                new Set([...prev.map((p) => p.value), ...newProductTypes])
            )
                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
            return merged.map((pt) => ({ label: pt, value: pt }));
          });
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    debouncedFetch();
    return () => {
      isMounted = false;
      clearTimeout(fetchTimeoutRef.current);
    };
  }, [location.search]);


  return (
    <>
      <div className="bg-white px-4 pb-1 mb-3 rounded-md">
        <ReportHeader title="Product Mix Report" />
        <ReportFilters
          includeSubEvents
          includeDates
          includeTime
          includeLocations
          includeVendors
          includeVendorExclusions
          eventScopedVendors
          includeUsernames
          includeProductTypes
          includeGrouping
          usernameOptions={initialUsernames}
          productTypeOptions={initialProductTypes}
          locationOptions={initialLocations}
          groupingOptions={displayTypeOptions}
          groupingValue={displayType}
          onGroupingChange={(value) => setDisplayType(value as DisplayType)}
          trailingSlot={
            <div className="flex items-end justify-end h-full">
              <SecondaryButton onClick={handleExportCSV}>
                Export to CSV
              </SecondaryButton>
            </div>
          }
          customQueryPaths={{
            dateFrom: "_and[0].transaction_hour",
            dateTo: "_and[1].transaction_hour",
          }}
        />
      </div>
      {!isPivot && (
        <>
          <div style={{ display: "flex", alignItems: "stretch", flexWrap: "wrap" }}>
            <ToggleButton
              isSelected={selectedDataKey === "total_net_sales"}
              onClick={() => handleDataKeyChange("total_net_sales")}
            >
              Total Net Sales
            </ToggleButton>
            <ToggleButton
              isSelected={selectedDataKey === "total_gross_sales"}
              onClick={() => handleDataKeyChange("total_gross_sales")}
            >
              Total Gross Sales
            </ToggleButton>
            <ToggleButton
              isSelected={selectedDataKey === "total_units_sold"}
              onClick={() => handleDataKeyChange("total_units_sold")}
            >
              Total Units Sold
            </ToggleButton>
          </div>
          <BarChartComponent rows={chartRows} selectedDataKey={selectedDataKey} displayType={displayType} />
        </>
      )}
      <div style={{ height: "60vh", marginTop: "4px" }}>
        <div
          className={"ag-theme-quartz"}
          style={{ width: "100%", height: "100%" }}
        >
          {isLoading ? (
              <Loader />
          ) : (
          <AgGridReact
            rowData={tableRows}
            columnDefs={currentColDefs}
            rowHeight={rowHeight}
            autoSizeStrategy={autoSizeStrategy}
            pagination={true}
            paginationPageSize={100}
            gridOptions={gridOptions}
            pinnedBottomRowData={totalRow}
            onGridReady={(params) => (gridRef.current = params.api)}
          />
        )}
        </div>
      </div>
    </>
  );
};
